import type { Shop, User, Owner } from "@shared/schema";

// Keys for localStorage
const CURRENT_SHOP_KEY = "currentShop";
const CURRENT_USER_KEY = "currentUser";
const CURRENT_OWNER_KEY = "currentOwner";
const OWNER_SHOPS_KEY = "ownerShops";
const ACTIVE_SHOP_ID_KEY = "activeShopId";

// Tipo para usuario en localStorage (puede ser parcial)
type StoredUser = Partial<User> & {
  id: string;
  shopId: string;
  role: string;
  fullName: string;
};

// Tipo para owner en localStorage
type StoredOwner = Partial<Owner> & {
  id: string;
  email: string;
  businessName: string;
  currency?: string;
  ownerType?: "simple" | "multi"; // <-- NUEVO CAMPO para diferenciar dueños
  planType?: string; // <-- Para compatibilidad
};

// Get current shop from localStorage
export function getCurrentShop(): Shop | null {
  if (typeof window === "undefined") return null;
  try {
    const data = localStorage.getItem(CURRENT_SHOP_KEY);
    // {Protegerse contra valores corruptos o "undefined" string}
    if (!data || data === "undefined" || data === "null") return null;
    return JSON.parse(data);
  } catch (error) {
    console.warn("⚠️ [getCurrentShop] Error parseando datos, limpiando...");
    localStorage.removeItem(CURRENT_SHOP_KEY);
    return null;
  }
}

// Set current shop in localStorage
export function setCurrentShop(shop: Shop): void {
  localStorage.setItem(CURRENT_SHOP_KEY, JSON.stringify(shop));
}

// Remove current shop
export function clearCurrentShop(): void {
  localStorage.removeItem(CURRENT_SHOP_KEY);
}

// Get current user from localStorage
export function getCurrentUser(): StoredUser | null {
  if (typeof window === "undefined") return null;
  try {
    const data = localStorage.getItem(CURRENT_USER_KEY);
    // {Protegerse contra valores corruptos}
    if (!data || data === "undefined" || data === "null") return null;
    return JSON.parse(data);
  } catch (error) {
    console.warn("⚠️ [getCurrentUser] Error parseando datos, limpiando...");
    localStorage.removeItem(CURRENT_USER_KEY);
    return null;
  }
}

// Set current user in localStorage
export function setCurrentUser(user: StoredUser): void {
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
}

// Remove current user
export function clearCurrentUser(): void {
  localStorage.removeItem(CURRENT_USER_KEY);
}

// Check if user has permission
export function hasPermission(
  requiredRole: "supervisor" | "store_manager" | "cashier",
): boolean {
  const user = getCurrentUser();
  if (!user) return false;

  const roleHierarchy = { supervisor: 3, store_manager: 2, cashier: 1 };
  return (
    roleHierarchy[user.role as "supervisor" | "store_manager" | "cashier"] >=
    roleHierarchy[requiredRole]
  );
}

// Get user role
export function getUserRole(): string | null {
  const user = getCurrentUser();
  return user ? user.role : null;
}

// Check if user is shop owner (logged in as shop)
export function isShopOwner(): boolean {
  const user = getCurrentUser();
  return user ? user.id.startsWith("shop_") : false;
}

// ============ OWNER FUNCTIONS ============

// Get current owner from localStorage with migration for existing owners
export function getCurrentOwner(): StoredOwner | null {
  if (typeof window === "undefined") return null;

  // ESTRATEGIA 1: Buscar en CURRENT_OWNER_KEY
  let data = localStorage.getItem(CURRENT_OWNER_KEY);
  let storageKey = CURRENT_OWNER_KEY;

  // ESTRATEGIA 2: Si no está ahí, buscar en "shopOwner" (para compatibilidad)
  if (!data) {
    data = localStorage.getItem("shopOwner");
    storageKey = "shopOwner";
  }

  if (!data) return null;

  try {
    // {Validar que data no sea string literal "undefined" o "null"}
    if (!data || data === "undefined" || data === "null") return null;
    const owner = JSON.parse(data);

    // ========== MIGRACIÓN: Si owner no tiene ownerType, asignarlo ==========
    if (owner && !owner.ownerType) {
      // Determinar tipo basado en múltiples factores
      let ownerType: "simple" | "multi" = "simple"; // Por defecto simple

      // REGLA 1: Verificar cantidad de tiendas - LA MÁS IMPORTANTE
      const ownerShopsData = localStorage.getItem(OWNER_SHOPS_KEY);
      if (ownerShopsData) {
        try {
          const shops = JSON.parse(ownerShopsData);
          if (shops && Array.isArray(shops)) {
            if (shops.length > 1) {
              ownerType = "multi";
            } else if (shops.length === 1) {
              ownerType = "simple";
            }
          }
        } catch (e) {
          console.warn("⚠️ [getCurrentOwner] Error parseando ownerShops:", e);
        }
      }

      // REGLA 2: Si ya determinamos por tiendas, usar eso. Si no, usar otras reglas
      if (ownerType === "simple") {
        // Solo aplicar estas reglas si no determinamos por tiendas
        if (owner.planType && owner.planType !== "basic") {
          ownerType = "multi";
        } else if (
          owner.businessName &&
          owner.ownerName &&
          owner.businessName !== owner.ownerName
        ) {
          ownerType = "multi";
        }
      }

      // Asignar el ownerType calculado
      owner.ownerType = ownerType;

      // Guardar de vuelta en localStorage (en ambos lugares para consistencia)
      localStorage.setItem(storageKey, JSON.stringify(owner));
      localStorage.setItem(CURRENT_OWNER_KEY, JSON.stringify(owner)); // También en currentOwner
    }

    return owner;
  } catch (error) {
    console.error("❌ [getCurrentOwner] Error parseando owner:", error);
    return null;
  }
}

// Get shop owner's currency (for shops) - VERSIÓN MEJORADA Y ROBUSTA
export async function getShopOwnerCurrency(): Promise<string> {
  if (typeof window === "undefined") return "CUP";

  // ========== ESTRATEGIA 1: shopOwner en localStorage ==========
  const shopOwnerJson = localStorage.getItem("shopOwner");
  if (shopOwnerJson) {
    try {
      const shopOwner = JSON.parse(shopOwnerJson);
      if (shopOwner && shopOwner.currency) {
        return shopOwner.currency;
      }
    } catch (error) {
      console.warn(
        "⚠️ [getShopOwnerCurrency] Error parseando shopOwner:",
        error,
      );
    }
  }

  // ========== ESTRATEGIA 2: currentOwner en localStorage ==========
  const ownerJson = localStorage.getItem(CURRENT_OWNER_KEY);
  if (ownerJson) {
    try {
      const owner = JSON.parse(ownerJson);
      if (owner && owner.currency) {
        return owner.currency;
      }
    } catch (error) {
      console.warn(
        "⚠️ [getShopOwnerCurrency] Error parseando currentOwner:",
        error,
      );
    }
  }

  // ========== ESTRATEGIA 3: Buscar owner por API usando currentShop.ownerId ==========
  const currentShopJson = localStorage.getItem("currentShop");
  if (currentShopJson) {
    try {
      const currentShop = JSON.parse(currentShopJson);
      if (currentShop && currentShop.ownerId) {
        try {
          // Intentar obtener owner desde la API
          const response = await fetch(`/api/owners/${currentShop.ownerId}`);
          if (response.ok) {
            const ownerData = await response.json();
            if (ownerData && ownerData.currency) {
              // Guardar en localStorage para futuras consultas
              localStorage.setItem("shopOwner", JSON.stringify(ownerData));
              return ownerData.currency;
            }
          }
        } catch (apiError) {
          console.warn(
            "⚠️ [getShopOwnerCurrency] Error obteniendo owner de API:",
            apiError,
          );
        }
      }
    } catch (error) {
      console.warn(
        "⚠️ [getShopOwnerCurrency] Error parseando currentShop:",
        error,
      );
    }
  }

  // ========== ESTRATEGIA 4: Valor por defecto con advertencia ==========
  console.warn(
    "⚠️ [getShopOwnerCurrency] No se pudo determinar la moneda, usando CUP por defecto",
  );
  return "CUP"; // Valor por defecto
}

// Versión síncrona para compatibilidad (usa la asíncrona internamente)
export function getShopOwnerCurrencySync(): string {
  const shopOwnerJson = localStorage.getItem("shopOwner");

  if (shopOwnerJson) {
    try {
      const shopOwner = JSON.parse(shopOwnerJson);

      if (shopOwner && shopOwner.currency) {
        return shopOwner.currency;
      }
    } catch (error) {
      console.error(
        "🔍 [getShopOwnerCurrencySync] Error parseando shopOwner:",
        error,
      );
    }
  }

  const ownerJson = localStorage.getItem(CURRENT_OWNER_KEY);
  if (ownerJson) {
    try {
      const owner = JSON.parse(ownerJson);
      if (owner && owner.currency) {
        return owner.currency;
      }
    } catch {
      // Ignorar error
    }
  }

  return "CUP";
}

// Set current owner in localStorage
export function setCurrentOwner(owner: StoredOwner): void {
  // Asegurar que el owner tenga currency
  const ownerWithCurrency = {
    ...owner,
    currency: owner.currency || "CUP",
  };
  localStorage.setItem(CURRENT_OWNER_KEY, JSON.stringify(ownerWithCurrency));
}

// Remove current owner
export function clearCurrentOwner(): void {
  localStorage.removeItem(CURRENT_OWNER_KEY);
  localStorage.removeItem(OWNER_SHOPS_KEY);
  localStorage.removeItem(ACTIVE_SHOP_ID_KEY);
}

// Get owner's shops from localStorage
export function getOwnerShops(): Shop[] {
  if (typeof window === "undefined") return [];
  const data = localStorage.getItem(OWNER_SHOPS_KEY);
  return data ? JSON.parse(data) : [];
}

// Set owner's shops in localStorage
export function setOwnerShops(shops: Shop[]): void {
  localStorage.setItem(OWNER_SHOPS_KEY, JSON.stringify(shops));
}

// Get active shop ID
export function getActiveShopId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(ACTIVE_SHOP_ID_KEY);
}

// Set active shop ID
export function setActiveShopId(shopId: string): void {
  localStorage.setItem(ACTIVE_SHOP_ID_KEY, shopId);
}

// Get active shop (combines activeShopId with ownerShops)
export function getActiveShop(): Shop | null {
  const activeShopId = getActiveShopId();
  if (!activeShopId) return null;

  const shops = getOwnerShops();
  return shops.find((shop) => shop.id === activeShopId) || null;
}

// Check if user is logged in as owner (not shop user)
export function isOwnerMode(): boolean {
  return getCurrentOwner() !== null;
}

// Enhanced logout for multi-tenant
export function logout(): void {
  clearCurrentUser();
  clearCurrentShop();
  clearCurrentOwner();
}

// Switch back to owner mode
export function switchToOwnerMode(): void {
  const owner = getCurrentOwner();
  if (owner) {
    // Keep owner data, clear shop/user context
    clearCurrentShop();
    clearCurrentUser();
    // Redirigir al dashboard de owner
    window.location.href = "/owner/dashboard";
  }
}

// Check if user can switch to owner mode
export function canSwitchToOwnerMode(): boolean {
  return getCurrentOwner() !== null && getCurrentShop() !== null;
}

// Check if user is in shop mode and can return to owner dashboard
export function isMultiTenantOwner(): boolean {
  const owner = getCurrentOwner();
  const currentShop = getCurrentShop();
  const currentUser = getCurrentUser();

  // Si no hay owner logueado, no puede cambiar a modo empresa
  if (!owner) {
    return false;
  }

  // Si no hay tienda actual, no está dentro de una tienda
  if (!currentShop) {
    return false;
  }

  // Si el usuario actual no tiene rol supervisor (el usuario temporal shop_ tiene role "supervisor")
  if (!currentUser || currentUser.role !== "supervisor") {
    return false;
  }

  // ============ NUEVA LÓGICA MÁS INCLUSIVA ============
  // TODO dueño que está dentro de una tienda puede volver al OwnerDashboard
  // SIN IMPORTAR su plan o cantidad de tiendas

  // Log para depuración
  console.log("🏢 [isMultiTenantOwner] Dueño detectado:", {
    ownerName: owner.ownerName,
    planType: owner.planType || "no especificado",
    shopName: currentShop.name,
    puedeVolver: true,
  });

  return true; // ← AHORA SIEMPRE DEVUELVE TRUE si hay owner, tienda y usuario admin
}

// ============ FUNCIÓN PARA VERIFICAR SI ES OWNER EN MODO TIENDA ============
export function isOwnerInShopMode(): boolean {
  const owner = getCurrentOwner();
  const currentShop = getCurrentShop();
  const currentUser = getCurrentUser();

  // Si hay owner, hay tienda actual, y el usuario actual tiene ID que empieza con "shop_"
  // eso significa que es un owner navegando en una de sus tiendas
  return !!(owner && currentShop && currentUser?.id?.startsWith("shop_"));
}

// ============ OBTENER PERMISOS DEL USUARIO ACTUAL ============
export function getCurrentUserPermissions(): {
  isOwner: boolean;
  isOwnerInShopMode: boolean;
  role: string | null;
} {
  const owner = getCurrentOwner();
  const user = getCurrentUser();
  const isOwnerMode = !!owner;
  const isInShopMode = !!user;

  return {
    isOwner: isOwnerMode && !isInShopMode, // Owner en su dashboard
    isOwnerInShopMode:
      isOwnerMode && isInShopMode && user?.id?.startsWith("shop_"), // Owner dentro de una tienda
    role: user?.role || null,
  };
}

// ============ VERSIÓN MEJORADA DE HASPERMISSION ============
export function hasPermissionEnhanced(
  requiredRole: "supervisor" | "store_manager" | "cashier",
): boolean {
  const { isOwnerInShopMode, role } = getCurrentUserPermissions();

  // Si es owner en modo tienda, TIENE TODOS LOS PERMISOS (es superadmin)
  if (isOwnerInShopMode) {
    return true;
  }

  // Si no, verificar el rol normal
  if (!role) return false;

  const roleHierarchy = { supervisor: 3, store_manager: 2, cashier: 1 };
  return (
    roleHierarchy[role as "supervisor" | "store_manager" | "cashier"] >=
    roleHierarchy[requiredRole]
  );
}

// ============ FUNCIÓN PARA VERIFICAR SUSPENSIÓN DEL OWNER ============
export async function checkOwnerSuspension(): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const currentShop = getCurrentShop();
  if (!currentShop) return false;

  try {
    // Hacer petición al backend para verificar si el owner está suspendido
    const response = await fetch(`/api/shops/${currentShop.id}/owner-status`);
    if (response.ok) {
      const data = await response.json();
      return data.isSuspended === true;
    }
  } catch (error) {
    console.error("Error verificando suspensión del owner:", error);
  }

  return false;
}

// Versión síncrona para usar en componentes que no pueden ser async
export function getOwnerSuspensionFromStorage(): boolean {
  if (typeof window === "undefined") return false;
  const suspended = localStorage.getItem("owner_suspended");
  return suspended === "true";
}

// Guardar estado de suspensión en localStorage
export function setOwnerSuspension(suspended: boolean): void {
  localStorage.setItem("owner_suspended", String(suspended));
}
